/**
 * CollectorUp business timezone formatting.
 *
 * DB stores timestamps in UTC (ISO strings). We convert to America/New_York
 * for every admin-facing render. Never trust the viewer's clock — the shop
 * runs on ET regardless of where the operator is sitting.
 *
 * Two format variants:
 *   - queueTwoLine(iso) → { date, time } for the wide desktop table
 *   - queueCompact(iso)  → single-string mobile fallback
 *   - detailLong(iso)    → "July 30, 2026 at 2:42 PM EDT" for detail pages
 *
 * All formatters return `null` for missing / unparseable input so the caller
 * can render "Date unavailable" without fabricating a value.
 */
const TZ = "America/New_York";

function _asDate(iso) {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** Returns { date: "Jul 30, 2026", time: "2:42 PM" } or null. */
export function queueTwoLine(iso) {
  const d = _asDate(iso);
  if (!d) return null;
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, month: "short", day: "numeric", year: "numeric",
  }).format(d);
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true,
  }).format(d);
  return { date, time };
}

/** Returns "Jul 30, 2:42 PM" or null — used on narrow screens. */
export function queueCompact(iso) {
  const d = _asDate(iso);
  if (!d) return null;
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, month: "short", day: "numeric",
  }).format(d);
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true,
  }).format(d);
  return `${date}, ${time}`;
}

/**
 * Long form for the Order Information section:
 *   "July 30, 2026 at 2:42 PM EDT"
 * The trailing timezone abbreviation (EDT/EST) makes it unambiguous for
 * operators who may be in a different zone.
 */
export function detailLong(iso) {
  const d = _asDate(iso);
  if (!d) return null;
  const dateFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, month: "long", day: "numeric", year: "numeric",
  });
  const timeFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true,
    timeZoneName: "short",
  });
  const date = dateFmt.format(d);
  const timeParts = timeFmt.formatToParts(d);
  const hour = timeParts.find((p) => p.type === "hour")?.value || "";
  const minute = timeParts.find((p) => p.type === "minute")?.value || "";
  const dayPeriod = timeParts.find((p) => p.type === "dayPeriod")?.value || "";
  const zone = timeParts.find((p) => p.type === "timeZoneName")?.value || "";
  return `${date} at ${hour}:${minute} ${dayPeriod} ${zone}`.trim();
}

/** Localised copy used when the timestamp is missing/unparseable. */
export const DATE_UNAVAILABLE = "Date unavailable";
